import { AlertCircle, Building2 } from 'lucide-react';
import { useEffect } from 'react';

import { categoryLabels } from '@/components/coverage/shared/constants';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CoverageSchema } from '@/services/types/coverage-types';

import { FIELD_STYLES } from './shared/EnhancedSelect';

interface CoveragePickerProps {
  coverages: CoverageSchema[];
  selectedCoverageId: string | undefined;
  onCoverageChange: (coverageId: string) => void;
  isLoading?: boolean;
  className?: string;
}

const getCoverageDisplayText = (coverage: CoverageSchema): string => {
  const parts: string[] = [];

  if (coverage.carrier?.name) {
    parts.push(coverage.carrier.name);
  }

  const policyNumber = coverage.policy_identifiers?.find(
    (id) => id.identifier_type === 'POLICY_NUMBER',
  );

  if (policyNumber?.identifier_value) {
    parts.push(policyNumber.identifier_value);
  }

  return parts.length > 0
    ? parts.join(' - ')
    : `Coverage ${coverage.id.slice(0, 8)}`;
};

const getCoverageDetails = (coverage: CoverageSchema) => {
  const details: string[] = [];

  if (coverage.category && categoryLabels[coverage.category]) {
    details.push(categoryLabels[coverage.category]);
  }

  if (coverage.policy_type) {
    details.push(coverage.policy_type);
  }

  return details;
};

/**
 * Enhanced select trigger for coverage display using shared styles
 */
const CoverageSelectTrigger = ({
  selectedCoverage,
  placeholder,
}: {
  selectedCoverage?: CoverageSchema;
  placeholder: string;
}) => (
  <SelectTrigger className={FIELD_STYLES.selectTrigger}>
    <SelectValue placeholder={placeholder}>
      {selectedCoverage && (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-500" />
          <div className="flex flex-col items-start">
            <span className="font-medium text-gray-900 leading-tight">
              {getCoverageDisplayText(selectedCoverage)}
            </span>
            {(() => {
              const details = getCoverageDetails(selectedCoverage);
              if (details.length > 0) {
                return (
                  <span className="text-sm text-gray-500 leading-tight">
                    {details.join(' • ')}
                  </span>
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}
    </SelectValue>
  </SelectTrigger>
);

export const CoveragePicker = ({
  coverages,
  selectedCoverageId,
  onCoverageChange,
  isLoading = false,
  className,
}: CoveragePickerProps) => {
  // Auto-select the single available coverage to streamline the flow
  useEffect(() => {
    if (!selectedCoverageId && coverages.length === 1) {
      onCoverageChange(coverages[0].id);
    }
  }, [coverages, selectedCoverageId, onCoverageChange]);

  if (isLoading) {
    return (
      <div className={className}>
        <div className="space-y-2">
          <span className="block text-sm font-medium text-gray-700">
            Select Policy
          </span>
          <div className="h-12 bg-gray-100 rounded-md animate-pulse" />
        </div>
      </div>
    );
  }

  if (!coverages || coverages.length === 0) {
    return (
      <div className={className}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No coverage policies found for this matter. Please add coverage
            information first.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (coverages.length === 1) {
    const coverage = coverages[0];
    const details = getCoverageDetails(coverage);

    return (
      <div className={className}>
        <div className="space-y-2">
          <span className="block text-sm font-medium text-gray-700">
            Policy Selected
          </span>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <div>
                <div className="font-medium text-blue-900 text-sm">
                  {getCoverageDisplayText(coverage)}
                </div>
                {details.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {details.map((detail, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="text-xs"
                      >
                        {detail}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedCoverage = coverages.find((c) => c.id === selectedCoverageId);

  return (
    <div className={className}>
      <div className="space-y-2">
        <span className="block text-sm font-medium text-gray-700">
          Select Policy
          <span className="text-red-500 ml-1">*</span>
        </span>
        <Select value={selectedCoverageId} onValueChange={onCoverageChange}>
          <CoverageSelectTrigger
            selectedCoverage={selectedCoverage}
            placeholder="Choose a coverage policy..."
          />
          <SelectContent className="max-h-64">
            {coverages.map((coverage) => {
              const details = getCoverageDetails(coverage);

              return (
                <SelectItem
                  key={coverage.id}
                  value={coverage.id}
                  className="cursor-pointer py-2"
                >
                  <div className="flex flex-col items-start w-full">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-gray-900">
                        {getCoverageDisplayText(coverage)}
                      </span>
                    </div>
                    {details.length > 0 && (
                      <div className="flex gap-1 mt-1 ml-6">
                        {details.map((detail, index) => (
                          <span key={index} className="text-xs text-gray-500">
                            {detail}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          All form fields will be populated with data from the selected policy.
        </p>
      </div>
    </div>
  );
};
